import { create, Fragment, type Child, type Component } from './dom.ts'

export { Fragment }

/**
 * The automatic JSX runtime. The compiler passes a `key` attribute as the third argument, not in
 * `props` (`jsxDEV` passes it the same way, followed by debug arguments this runtime ignores). A
 * component gets it back as a prop, so `<For each={…} key={(x) => x.id}>` keys its rows. An element
 * has no use for it, so it is dropped rather than written as an attribute.
 */
export function jsx(type: string | Component<any> | typeof Fragment, props: Record<string, unknown>, key?: unknown): Child {
  if (key !== undefined && typeof type === 'function') props = { ...props, key }
  return create(type, props)
}

export const jsxs = jsx
export const jsxDEV = jsx

type Reactive<T> = T | (() => T)

interface BaseProps {
  children?: Child
  ref?: (element: any) => void
  class?: Reactive<string | undefined>
  classList?: Record<string, Reactive<boolean>>
  style?: Reactive<string> | Record<string, Reactive<string | number | null | undefined>>
  id?: string
  title?: Reactive<string | undefined>
  role?: string
  tabIndex?: number
  hidden?: Reactive<boolean>
  [key: `on${string}`]: ((event: any) => void) | undefined
  [key: `aria-${string}`]: Reactive<string | boolean | undefined>
  [key: `data-${string}`]: Reactive<string | number | boolean | undefined>
  [key: string]: unknown
}

export namespace JSX {
  export type Element = Child
  export interface ElementChildrenAttribute { children: {} }
  export interface IntrinsicElements { [tag: string]: BaseProps }
}
